import React, { useState, useEffect, Suspense } from "react";
import { connect } from "react-redux";
import { ApolloProvider, useLazyQuery, useSubscription } from "@apollo/client";
import { BrowserRouter as Router, Route, Switch, useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";
import Signup from "./containers/Auth/Signup";
// import Signin from "./containers/Auth/Signin";
import ResetPassword from "./containers/Auth/ResetPassword";
import OTP from "./containers/Auth/OTP";
import Dashboard from "./containers/Dashboard";
import Sales from "./containers/Sales";
import ScanQrCode from "./containers/Sales/_partials/ScanQrCode";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import addNotification from 'react-push-notification';
import KDSSigninForm from "./components/kitchenScreen/loginScreen";
// import Cohere from "cohere-js";

// Redux
import store from "./redux/store";
import { setCurrentUser, signoutUser } from "./redux/actions/authActions";
import { setNetworkStatus } from "./redux/actions/networkStatusActions";
import { toggleSubscribeModal, toggleRenewalModal, checkSubscriptionStatus } from "./redux/actions/renewalActions";
import { getPricingPlanData } from "./redux/actions/merchantAction";
import setAuthToken from "./utils/setAuthToken";

// Components
import SubscriptionModal from "./components/StripePayment/subscriptionModal";
import NewSubscriptionModal from "./components/StripePayment/NewSubscriptionModal";
import RenewalModal from "./containers/Sales/_partials/RenewalModal";

import "./App.scss";
import "@ant-design/compatible/assets/index.css";
import {
  deleteOfflineSale,
  notifyMerchant,
  recordOfflineSale,
} from "./utils/backgroundFunctions";
import client from "./utils/client";
import api from "./api";
import ResetCode from "./containers/Auth/ResetPassword/ResetCode";
import { notification, Button, Spin } from "antd";
import { notification2, notification25 } from "./noticationSounds";
import { PAY_ONE_ORDER, SUBSCRIBE_INSTORE_ORDERS, SUBSCRIBE_LOYSTAR_BOOKINGS } from "./constants";
import { openOrderScreen as activateKitchenScreen } from "./redux/actions/orderActions";
// import { Provider, ErrorBoundary } from "@rollbar/react";

import KitchScreen from "./components/kitchenScreen";
import SupplierPage from "./containers/SupplierPage";
import rollbar from "./utils/rollbar";

// Enterprise Loyalty imports
import EnterpriseLogin from "./containers/Enterprise/Auth/EnterpriseLogin";
import EnterpriseSignup from "./containers/Enterprise/Auth/EnterpriseSignup";
import EnterpriseDashboard from "./containers/Enterprise/Dashboard/EnterpriseDashboard";
import EnterprisePrivateRoute from "./containers/Enterprise/components/EnterprisePrivateRoute";

const Signin = React.lazy(() => import("./containers/Auth/Signin"));
const access_token = JSON.parse(localStorage.getItem("loystar-access-token"));
const multiLevelToken = localStorage.getItem("multi-level-token");
const mtierToken = localStorage.getItem("mtier-token");

// Cohere.init("RtbpsNLCYdWkF67C2BzDjDVg");

const rollbarConfig = {
  accessToken: typeof process !== 'undefined' && process.env ? process.env.REACT_APP_ROLLABAR_ACCESSTOKEN : '',
  environment: typeof process !== 'undefined' && process.env ? process.env.REACT_APP_ROLLBAR_ENVIRONMENT : ''
}

class ErrorBoundary extends React.Component {
  componentDidCatch(error, info) {
    rollbar.error(error, info);
  }

  render() {
    return this.props.children;
  }
}

if (access_token !== "undefined" && access_token !== null) {
  // Set auth token header auth
  setAuthToken({ ...access_token, mtier: mtierToken });

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));

  // Set user and isAuthenticated
  store.dispatch(setCurrentUser(userDetails));

  const signupDate = new Date(userDetails.created_at).getTime();
  const isDateBeforeSeptember12th2023 = signupDate < new Date('2023-09-12').getTime();
  
  window.usetifulTags = {
      userId: userDetails.email,
      segment: userDetails.business_type || userDetails.employer?.business_type,
      language: localStorage.getItem("current-language") || "English",
      role: userDetails.role_id === 1 ? "Admin" : userDetails.role?.name,
      firstName: userDetails.first_name || userDetails.username,
      currency: userDetails.currency || userDetails.employer?.currency,
      plan: userDetails.subscription_plan === "Free" ? "free" : "paid",
      isDateBeforeSeptember12th2023: isDateBeforeSeptember12th2023 ? "true" : "false",
  };

  window.$crisp?.push(["set", "user:email", userDetails.email]);
  window.$crisp?.push([
    "set",
    "user:nickname",
    userDetails.first_name || userDetails.username,
  ]);

  const currentTime = Date.now() / 1000;

  // Check for token expiration
  if (access_token.expiry < currentTime) {
    // Sign out user
    store.dispatch(signoutUser());
  }
}

const isExpired = (token) => {
  const jwtToken = jwt_decode(token);
  // console.log(jwtToken);
  if (jwtToken.exp < new Date().getTime() / 1000) {
    return true;
  }

  return false;
};
// nothing new to add
if (multiLevelToken !== "undefined" && multiLevelToken !== null) {
  if (isExpired(multiLevelToken)) {
    store.dispatch(signoutUser());
  }

  api.HttpClient.defaults.headers.common["multi-level-token"] = multiLevelToken;
}

const App = ({ 
  auth, 
  displayOrder, 
  isOrderPaid, 
  paidID,
  isSubscribeModalOpen,
  isRenewalModalOpen,
  toggleSubscribeModal,
  toggleRenewalModal,
  checkSubscriptionStatus,
  getPricingPlanData
}) => {
  const homeBranch = localStorage.getItem("homebranch") ? JSON.parse(localStorage.getItem("homebranch")) : {id: null};
  // const [width, setWidth] = useState(0);
  const [isOnline, setIsOnline] = useState(false);
  const [syncError, setSyncError] = useState([]);
  
  const [ incomingOrder, setIncomingOrder ] = useState(null);
  
  // Subscription modal state for Free plan users
  const [subscriptionPlanPrice, setSubscriptionPlanPrice] = useState({});
  const [freePlanUser, setFreePlanUser] = useState(null);
  const [isTrialModeActive, setIsTrialModeActive] = useState(false);

  // Destructure auth before using in useEffect dependencies
  const { isAuthenticated, user } = auth;

  // Constants for trial period
  const TRIAL_PERIOD_DAYS = 14;
  const milisecondsToDaysConst = 86400000;

  // Helper function to check if current route is public (no subscription modal)
  const isPublicRoute = () => {
    const publicRoutes = ['/', '/signup', '/signin', '/reset-password', '/reset-code', '/otp', '/loystar-kds', '/signin-kds'];
    const currentPath = window.location.pathname;
    const isPublic = publicRoutes.includes(currentPath);
    
    return isPublic;
  };

  // Helper function to check if trial has expired
  const isTrialExpired = (business) => {
    if (!business || !business.subscription_expires_on) return false;
    const daysLeft = Math.ceil(
      (new Date(business.subscription_expires_on) - Date.now()) / milisecondsToDaysConst
    );
    return business.subscription_plan === "Free" && daysLeft <= 0;
  };

  const updateNetwork = () => {
    store.dispatch(setNetworkStatus(navigator.onLine));
    setIsOnline(navigator.onLine);
  };
  const [ payOneOrder, { loading: payOrderLoading, error: payOneOrderError, data: payOrderData }] = useLazyQuery(PAY_ONE_ORDER);

  // Check if user is on Free plan and show subscription modal ONLY if trial expired
  const checkFreePlanUser = () => {
    if (!isAuthenticated || !user) {
      return;
    }
    
    // Don't show modal on public routes
    if (isPublicRoute()) {
      return;
    }
    
    const business = user.employer ? user.employer : user;
    
    // Check if user is on Free plan AND trial has expired
    // During active trial, users can use the app freely - the banner handles upgrade prompts
    // DISABLED: Auto-popup subscription modal for first subscription logic
    // if (business.subscription_plan === "Free" && isTrialExpired(business)) {
    //   // Trial has expired - enforce subscription modal
    //   setFreePlanUser(business);
    //   
    //   // Show the new unified subscription modal after a short delay
    //   setTimeout(() => {
    //     toggleSubscribeModal();
    //   }, 1000);
    // }
  };

  // Check for new signup subscription modal trigger
  const checkNewSignupSubscriptionModal = () => {
    // Don't show modal on public routes
    if (isPublicRoute()) {
      return;
    }
    
    const triggerModal = localStorage.getItem('triggerSubscriptionModal');
    const newSignupUser = localStorage.getItem('newSignupUser');
    const subscriptionPlanPrice = localStorage.getItem('subscriptionPlanPrice');
    const isTrialMode = localStorage.getItem('isTrialMode');

    // Check for new signup trigger
    if (triggerModal === 'true' && newSignupUser) {
      // Clear the trigger to prevent repeated shows
      localStorage.removeItem('triggerSubscriptionModal');
      localStorage.removeItem('newSignupUser');
      localStorage.removeItem('subscriptionPlanPrice');
      localStorage.removeItem('isTrialMode');

      const user = JSON.parse(newSignupUser);
      const planPricing = subscriptionPlanPrice ? JSON.parse(subscriptionPlanPrice) : {};

      // Store the data for the subscription modal
      setFreePlanUser(user);
      setSubscriptionPlanPrice(planPricing);
      
      // Set trial mode if flagged
      if (isTrialMode === 'true') {
        setIsTrialModeActive(true);
      }

      // Show the subscription modal after a short delay
      setTimeout(() => {
        toggleSubscribeModal();
      }, 1500);
    } else {
      // Check if current user is on Free plan and show subscription modal
      checkFreePlanUser();
    }
  };

  const { data: inSData } = useSubscription(SUBSCRIBE_INSTORE_ORDERS, {
    onData: (data)=>{
      const newOrdered =  data.data.data.subscribeInstoreOrders;
      setIncomingOrder(newOrdered)

      newOrdered && addNotification({
        title: `New Order: ${newOrdered.order_id} from ${newOrdered.customer.first_name}`,
        message: `Order ${newOrdered.order_id} just arrived from ${newOrdered.customer.first_name } with phone: ${newOrdered.customer.phone_number }. Tap here to start processing`,
        theme: 'darkblue',
        backgroundTop: 'green',
        native: true,
        duration: 300000, 
        onClick: ()=> window.location = "/d/orders"
      });

      
      notification.success({
        message:  `Order ${newOrdered.order_id} just arrived from ${newOrdered.customer.first_name } with phone: ${newOrdered.customer.phone_number }. Tap here to start processing`,
        placement: "topRight",
        duration: null,
        onClick: ()=> {
          window.location = "/d/orders"
        }
      });

      const audio = new Audio();
      audio.src ="https://www.loystar.co/wp-content/uploads/2023/05/Happybellmerge.mp3"
      audio.play();

     },
      variables: { merchant_id: user?.employer ? user?.employer?.id  : user.id, branch_id: user?.business_branch ? user?.business_branch.id : homeBranch.id  }
  });

  // const { data: bookingData } = useSubscription(SUBSCRIBE_LOYSTAR_BOOKINGS, {
  //   onData: (data)=>{
  //     console.log("jfj", data);
      
  //    },
  //     variables: { merchant_id: user?.employer ? user?.employer.id  : user?.id }
  // });
//  user.role_id === 1 ? user?.id : user?.employer.id
  useEffect(()=>{
    displayOrder && addNotification({
      title: displayOrder.title,
      message: displayOrder.message,
      theme: 'darkblue',
      backgroundTop: 'green',
      native: true,
      duration: 300000, 
    });
    displayOrder && notification.success({
      message:  displayOrder.message,
      placement: "topRight"
    })
    // Play notification sound when order arrives
    if (displayOrder) {
      try {
        const audio = new Audio();
        audio.src = "https://www.loystar.co/wp-content/uploads/2023/05/Happybellmerge.mp3";
        audio.play().catch((err) => console.error("Audio play error:", err));
      } catch (error) {
        console.error("Error playing notification sound:", error);
      }
    }
  }, [displayOrder]);

  
//to set order state to paid
  useEffect(()=>{
    isOrderPaid && payOneOrder({variables: {id: paidID}});
  }, [paidID]);

  // Initialize subscription status when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Check subscription status for banner display
      checkSubscriptionStatus(user.id);
    }
  }, [isAuthenticated, user]);

  // Check for subscription modal when user is authenticated
  useEffect(() => {
    // Don't run modal logic on public routes at all
    if (isPublicRoute()) {
      // Clear any free plan user state when on public routes
      if (freePlanUser) {
        setFreePlanUser(null);
      }
      return;
    }
    
    if (isAuthenticated && user) {
      // Check for new signup or Free plan user subscription modal
      checkNewSignupSubscriptionModal();
    }
  }, [isAuthenticated, user]);

  // Additional useEffect to handle modal state for Free plan users
  useEffect(() => {
    // Don't run modal logic on public routes at all
    if (isPublicRoute()) {
      // Clear any free plan user state when on public routes
      if (freePlanUser) {
        setFreePlanUser(null);
      }
      return;
    }
    
    if (isAuthenticated && user) {
      const business = user.employer ? user.employer : user;
      
      // If user is on Free plan and both modals are closed (but not through successful subscription), reopen appropriate modal
      if (business.subscription_plan === "Free" && !isSubscribeModalOpen && !isRenewalModalOpen && freePlanUser) {
        // Reopen appropriate modal based on currency after a short delay
        setTimeout(() => {
          // Check currency to determine which modal to reopen
          if (business.currency === "NGN") {
            toggleRenewalModal();
          } else {
            toggleSubscribeModal();
          }
        }, 2000);
      }
    }
  }, [isSubscribeModalOpen, isRenewalModalOpen, isAuthenticated, user, freePlanUser]);

  // Clean up modal state when navigating to public routes
  useEffect(() => {
    if (isPublicRoute()) {
      // Close any open subscription modal
      if (isSubscribeModalOpen) {
        toggleSubscribeModal(false);
      }
      
      // Close any open renewal modal
      if (isRenewalModalOpen) {
        toggleRenewalModal(false);
      }
      
      // Clear free plan user state
      if (freePlanUser) {
        setFreePlanUser(null);
      }
    }
  }, [isSubscribeModalOpen, isRenewalModalOpen, freePlanUser, toggleSubscribeModal, toggleRenewalModal]);

  useEffect(() => {
    async function fetchData() {
      const offlineSales = JSON.parse(localStorage.getItem("offlineSales"));

      const hasOfflineSales = offlineSales && offlineSales.length;

      window.addEventListener("online", updateNetwork);
      window.addEventListener("offline", updateNetwork);

      if (isAuthenticated && isOnline && hasOfflineSales) {
        // Loop Through Sales then POST to Server then notify user, all sales have been synced
        const requests = offlineSales.map((sale) => {
          const {
            transactionObj,
            cartList,
            addLoyaltyProgramToSale,
            shared_loyalty_txn,
            payment_reference,
          } = sale.payload;

          return recordOfflineSale(
            transactionObj,
            cartList,
            addLoyaltyProgramToSale,
            shared_loyalty_txn,
            payment_reference,
            sale.formattedData.created_at
          )
            .then((res) => {
              // Remove Sale from LocalStorage
              deleteOfflineSale(sale.formattedData.id);

              return { status: "success", data: res.data };
            })
            .catch((err) => {
              setSyncError((prevState) => [
                ...prevState,
                { status: "error", error: err, sale },
              ]);

              console.log({ status: "error", error: err, sale });
              return { status: "error", error: err.response, sale };
            });
        });

        try {
          await Promise.all(requests);

          if (!syncError.length) {
            notifyMerchant(
              "You are back online! All your offline sales have been synced!",
              "success"
            );
          } else {
            notifyMerchant(
              "An error occurred while syncing your offline sales. Some sales may not be synced!",
              "error"
            );
          }
        } catch (error) {
          console.log(error);
        }
      }
    }

    fetchData();

    return () => {
      window.removeEventListener("online", updateNetwork);
      window.removeEventListener("offline", updateNetwork);
    };
  }, [isOnline, isAuthenticated]);

  const fallBackDiv = (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center", 
      justifyContent: "center",
      backgroundColor: "#fff5f5",
      height: "100vh"
    }}>
      <Spin size="large"/>
      <h2 style={{ fontSize: "20px"}}>Loading....</h2>
    </div>
  )

  // try{
    return (
      <div className="App">
          {/* <Provider config={rollbarConfig}> */}
            <ErrorBoundary>
              <Router>
                <Suspense fallback={fallBackDiv}>              
                <ScrollToTop>
                  <Switch>
                    {/* Enterprise Loyalty Routes */}
                    <Route exact path="/e/login" component={EnterpriseLogin} />
                    <Route exact path="/e/signup" component={EnterpriseSignup} />
                    <EnterprisePrivateRoute path="/e" component={EnterpriseDashboard} />
                    
                    {/* Regular Merchant Routes */}
                    <Route exact path="/" component={Signin} />
                    <Route exact path="/d/purchase-orders/:uuid" component={SupplierPage} />
                    <PrivateRoute path="/d" component={Dashboard} />
                    <PrivateRoute path="/me" component={Dashboard} />
                    <Route exact path="/signup" component={Signup} />
                    <Route exact path="/signin" component={Signin} />
                    <Route exact path="/otp" component={OTP} />
                    <Route exact path="/loystar-kds" component={KitchScreen} />
                    <Route exact path="/signin-kds" component={KDSSigninForm} />
                    
                    <PrivateRoute exact path="/sales" component={Sales} />
                    <PrivateRoute exact path="/qr" component={ScanQrCode} />
                    <Switch>
                      <Route exact path="/reset-password" component={ResetPassword} />
                      <Route exact path="/reset-code" component={ResetCode} />
                    </Switch>
                  </Switch>
                </ScrollToTop>
                </Suspense>
              </Router>
            </ErrorBoundary>
          {/* </Provider> */}

        {/* Global Subscription Modal for Free Plan Users - Undismissable */}
        {/* New Unified Subscription Modal - Works for both Stripe and Paystack */}
        {freePlanUser && (isSubscribeModalOpen || isRenewalModalOpen) && (
          <NewSubscriptionModal
            isOpen={isSubscribeModalOpen || isRenewalModalOpen}
            onClose={() => {
              if (isSubscribeModalOpen) toggleSubscribeModal();
              if (isRenewalModalOpen) toggleRenewalModal();
              setIsTrialModeActive(false);
            }}
            user={freePlanUser}
            isUndismissableForFreePlan={!isTrialModeActive}
            isTrialMode={isTrialModeActive}
            onTrialPaymentSuccess={() => {
              // After successful trial payment, close modal and refresh
              if (isSubscribeModalOpen) toggleSubscribeModal();
              if (isRenewalModalOpen) toggleRenewalModal();
              setIsTrialModeActive(false);
              // Redirect to dashboard with success message
              window.location.href = '/d/sales?trial=success';
            }}
          />
        )}

        {/* Legacy modals - kept for backward compatibility */}
        {/* Uncomment below and comment above to use old modals */}
        {/* {freePlanUser && isSubscribeModalOpen && (
          <SubscriptionModal
            subscriptionPlanPrice={subscriptionPlanPrice}
            getSubscription={checkSubscriptionStatus}
            hasActiveSubscription={false}
            user={freePlanUser}
            isUndismissableForFreePlan={true}
            toggleSubscribeModal={() => {
              toggleSubscribeModal();
            }}
          />
        )}

        {freePlanUser && isRenewalModalOpen && (
          <RenewalModal
            user={freePlanUser}
            isUndismissableForFreePlan={true}
            toggleRenewalModal={() => {
              toggleRenewalModal();
            }}
          />
        )} */}
      </div>
    );
  // }catch(err){
  //   <div className="refresh-btn-div">
  //     <Button
  //       type="primary"
  //       onClick={() => window.location.reload(false)}
  //     >
  //       Refresh
  //     </Button>
  //   </div>
  // }
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  displayOrder: state.allOrders.displayOrder,
  isOrderPaid: state.allOrders.isOrderPaid,
  paidID: state.allOrders.paidID,
  openOrderScreen: state.allOrders.openOrderScreen,
  isKitchenScreenActive: state.appStore.isKitchenScreenActive,
  isSubscribeModalOpen: state.renewal.isSubscribeModalOpen,
  isRenewalModalOpen: state.renewal.isRenewalModalOpen
});

export default connect(mapStateToProps, {
  activateKitchenScreen,
  toggleSubscribeModal,
  toggleRenewalModal,
  checkSubscriptionStatus,
  getPricingPlanData
})(App);
